import { useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { AlertCircle, Camera, CheckCircle2, Loader2, ShieldCheck, Upload, UserRound, UsersRound, Wand2 } from 'lucide-react'
import { formatDateLong } from '@hotel/shared/utils'
import {
  useExtractPublicCheckInPhoto,
  usePublicCheckInSession,
  useSubmitPublicCheckIn,
  useUploadPublicCheckInPhoto,
} from '../api/hooks'
import type {
  HotelProfile,
  PublicCheckInCompanion,
  PublicCheckInPhoto,
  PublicCheckInPrimary,
  PublicCheckInSession,
  MigrationSex,
  ReservationDocumentType,
} from '../api/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'

const documentLabels: Record<ReservationDocumentType, string> = {
  dni: 'DNI / Identidad',
  passport: 'Pasaporte',
  license: 'Licencia',
  other: 'Otro',
}

const sexLabels: Record<Exclude<MigrationSex, ''>, string> = {
  M: 'Masculino',
  F: 'Femenino',
}

const defaultMigrationDestination = 'Comayagua'

const blankPrimary: PublicCheckInPrimary = {
  first_name: '',
  last_name: '',
  document_type: 'dni',
  document_number: '',
  date_of_birth: '',
  address: '',
  city: '',
  country: 'Honduras',
  sex: '',
  document_issue_country: '',
  document_expiration_date: '',
  migration_nationality: '',
  migration_origin: '',
  migration_destination: defaultMigrationDestination,
}

function normalizeCompanion(index: number, companion?: Partial<PublicCheckInCompanion>): PublicCheckInCompanion {
  return {
    key: companion?.key || `companion-${index + 1}`,
    full_name: companion?.full_name || '',
    document_type: companion?.document_type || 'dni',
    document_number: companion?.document_number || '',
    sex: companion?.sex || '',
    date_of_birth: companion?.date_of_birth || '',
    document_issue_country: companion?.document_issue_country || '',
    document_expiration_date: companion?.document_expiration_date || '',
    migration_nationality: companion?.migration_nationality || '',
    migration_origin: companion?.migration_origin || '',
    migration_destination: companion?.migration_destination || defaultMigrationDestination,
  }
}

export function PublicCheckInPage({ hotel }: { hotel?: HotelProfile }) {
  const { token } = useParams<{ token: string }>()
  const sessionQuery = usePublicCheckInSession(token)
  const uploadPhoto = useUploadPublicCheckInPhoto(token)
  const extractPhoto = useExtractPublicCheckInPhoto(token)
  const submitCheckIn = useSubmitPublicCheckIn(token)

  const [initialized, setInitialized] = useState(false)
  const [primary, setPrimary] = useState<PublicCheckInPrimary>(blankPrimary)
  const [companions, setCompanions] = useState<PublicCheckInCompanion[]>([])
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const session = sessionQuery.data

  useEffect(() => {
    if (!session || initialized) return
    if (session.primary) {
      setPrimary({
        ...blankPrimary,
        ...session.primary,
        date_of_birth: session.primary.date_of_birth || '',
        country: session.primary.country || 'Honduras',
        sex: session.primary.sex || '',
        document_issue_country: session.primary.document_issue_country || '',
        document_expiration_date: session.primary.document_expiration_date || '',
        migration_nationality: session.primary.migration_nationality || '',
        migration_origin: session.primary.migration_origin || '',
        migration_destination: (
          session.primary.migration_destination || defaultMigrationDestination
        ),
      })
    } else {
      setPrimary(blankPrimary)
    }
    setCompanions(
      Array.from({ length: session.max_companions }, (_, index) =>
        normalizeCompanion(index, session.companions[index]),
      ),
    )
    setInitialized(true)
  }, [initialized, session])

  const activeCompanions = useMemo(
    () => companions.filter((companion) => companion.full_name.trim()),
    [companions],
  )

  const canSubmit = Boolean(
    primary.first_name.trim() &&
    primary.last_name.trim() &&
    acceptedTerms &&
    !submitCheckIn.isPending,
  )

  const handlePrimaryChange = (field: keyof PublicCheckInPrimary, value: string) => {
    setPrimary((prev) => ({ ...prev, [field]: value }))
  }

  const handleCompanionChange = (
    index: number,
    field: keyof PublicCheckInCompanion,
    value: string,
  ) => {
    setCompanions((prev) =>
      prev.map((companion, idx) =>
        idx === index ? { ...companion, [field]: value } : companion,
      ),
    )
  }

  const applySuggestions = (
    photo: PublicCheckInPhoto,
    suggestions: Partial<PublicCheckInPrimary>,
  ) => {
    if (photo.person_type === 'primary') {
      setPrimary((prev) => ({
        ...prev,
        ...suggestions,
        document_type: suggestions.document_type || prev.document_type,
        date_of_birth: suggestions.date_of_birth || prev.date_of_birth || '',
        country: suggestions.country || prev.country || 'Honduras',
        sex: suggestions.sex || prev.sex || '',
        document_issue_country: (
          suggestions.document_issue_country ||
          prev.document_issue_country ||
          ''
        ),
        document_expiration_date: (
          suggestions.document_expiration_date ||
          prev.document_expiration_date ||
          ''
        ),
        migration_nationality: (
          suggestions.migration_nationality || prev.migration_nationality || ''
        ),
        migration_origin: suggestions.migration_origin || prev.migration_origin || '',
        migration_destination: (
          suggestions.migration_destination ||
          prev.migration_destination ||
          defaultMigrationDestination
        ),
      }))
    } else {
      const fullName = [suggestions.first_name, suggestions.last_name].filter(Boolean).join(' ')
      setCompanions((prev) =>
        prev.map((companion) =>
          companion.key === photo.person_key
            ? {
                ...companion,
                full_name: fullName || companion.full_name,
                document_type: suggestions.document_type || companion.document_type,
                document_number: suggestions.document_number || companion.document_number,
                sex: suggestions.sex || companion.sex || '',
                date_of_birth: suggestions.date_of_birth || companion.date_of_birth || '',
                document_issue_country: (
                  suggestions.document_issue_country ||
                  companion.document_issue_country ||
                  ''
                ),
                document_expiration_date: (
                  suggestions.document_expiration_date ||
                  companion.document_expiration_date ||
                  ''
                ),
                migration_nationality: (
                  suggestions.migration_nationality ||
                  companion.migration_nationality ||
                  ''
                ),
                migration_origin: (
                  suggestions.migration_origin || companion.migration_origin || ''
                ),
                migration_destination: (
                  suggestions.migration_destination ||
                  companion.migration_destination ||
                  defaultMigrationDestination
                ),
              }
            : companion,
        ),
      )
    }
  }

  const extractAndApply = async (photo: PublicCheckInPhoto) => {
    const result = await extractPhoto.mutateAsync({ photoId: photo.id })
    applySuggestions(photo, result.suggestions || {})
  }

  const handleUpload = async (
    file: File,
    personType: 'primary' | 'companion',
    personKey: string,
  ) => {
    setSubmitError('')
    try {
      const uploadedPhoto = await uploadPhoto.mutateAsync({
        file,
        person_type: personType,
        person_key: personKey,
      })
      try {
        await extractAndApply(uploadedPhoto)
      } catch {
        setSubmitError('La foto se subió, pero no se pudieron leer los datos del documento.')
      }
      await sessionQuery.refetch()
    } catch {
      setSubmitError('No se pudo subir la imagen. Intenta de nuevo.')
    }
  }

  const handleExtract = async (photo: PublicCheckInPhoto) => {
    setSubmitError('')
    try {
      await extractAndApply(photo)
      await sessionQuery.refetch()
    } catch {
      setSubmitError('No se pudieron leer los datos del documento.')
    }
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitError('')
    try {
      await submitCheckIn.mutateAsync({
        primary: {
          ...primary,
          date_of_birth: primary.date_of_birth || null,
          document_expiration_date: primary.document_expiration_date || null,
        },
        companions: activeCompanions.map((companion) => ({
          ...companion,
          date_of_birth: companion.date_of_birth || null,
          document_expiration_date: companion.document_expiration_date || null,
          migration_destination: (
            companion.migration_destination || defaultMigrationDestination
          ),
        })),
        accepted_terms: acceptedTerms,
      })
      await sessionQuery.refetch()
    } catch {
      setSubmitError('No se pudo completar el check-in. Revisa los datos.')
    }
  }

  if (sessionQuery.isLoading) {
    return (
      <StandaloneShell hotel={hotel}>
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-terracotta" />
        </div>
      </StandaloneShell>
    )
  }

  if (sessionQuery.isError || !session) {
    return (
      <StandaloneShell hotel={hotel}>
        <Card>
          <CardContent className="flex items-start gap-3 p-5 sm:p-6">
            <AlertCircle className="mt-1 h-5 w-5 shrink-0 text-red-600" />
            <div>
              <h1 className="font-heading text-xl font-bold text-wood-dark sm:text-2xl">
                Enlace no disponible
              </h1>
              <p className="mt-2 text-sm text-stone">
                El enlace de check-in no existe o ya expiró.
              </p>
            </div>
          </CardContent>
        </Card>
      </StandaloneShell>
    )
  }

  const submitResult = submitCheckIn.data
  const completed = session.already_submitted || Boolean(submitResult)

  return (
    <StandaloneShell hotel={hotel}>
      <div className="space-y-4 sm:space-y-5">
        <div>
          <Badge variant={session.can_auto_check_in ? 'gold' : 'outline'}>
            {session.can_auto_check_in ? 'Check-in elegible' : 'Pre check-in'}
          </Badge>
          <h1 className="mt-3 font-heading text-2xl font-bold text-wood-dark sm:text-3xl">
            Check-in en línea
          </h1>
          <p className="mt-1 text-sm text-stone">
            Reservación {session.reservation.confirmation_code} · Habitación{' '}
            {session.reservation.room_number}
          </p>
        </div>

        {completed ? (
          <Card>
            <CardContent className="flex items-start gap-3 p-6">
              <CheckCircle2 className="mt-1 h-6 w-6 shrink-0 text-green-700" />
              <div>
                <h2 className="font-heading text-2xl font-bold text-wood-dark">
                  Datos recibidos
                </h2>
                <p className="mt-2 text-sm text-stone">
                  {submitResult?.auto_checked_in
                    ? 'Tu check-in fue completado automáticamente.'
                    : 'Tus datos quedaron guardados para recepción.'}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : null}

        <ReservationSummaryCard session={session} />

        {!completed ? (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <UserRound className="h-5 w-5" />
                  Titular
                </CardTitle>
                <CardDescription>Foto del documento, lectura y revisión de datos.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <PhotoPanel
                  photos={session.photos.filter((photo) => photo.person_type === 'primary')}
                  personType="primary"
                  personKey=""
                  onUpload={handleUpload}
                  onExtract={handleExtract}
                  busy={uploadPhoto.isPending || extractPhoto.isPending}
                />
                <div className="flex items-center gap-2 rounded-md bg-cream-light px-3 py-2 text-xs text-stone">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-terracotta" />
                  <span>
                    Enlace enviado por WhatsApp a{' '}
                    <span className="font-medium text-wood-dark">{session.phone_masked}</span>
                  </span>
                </div>
                <PrimaryFields value={primary} onChange={handlePrimaryChange} />
              </CardContent>
            </Card>

            {companions.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <UsersRound className="h-5 w-5" />
                    Acompañantes
                  </CardTitle>
                  <CardDescription>
                    {companions.length} espacio(s) disponible(s) según la reservación.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  {companions.map((companion, index) => (
                    <div key={companion.key} className="rounded-md border border-cream-dark p-4">
                      <h3 className="mb-3 font-medium text-wood-dark">
                        Acompañante {index + 1}
                      </h3>
                      <div className="mb-4">
                        <PhotoPanel
                          photos={session.photos.filter(
                            (photo) =>
                              photo.person_type === 'companion' &&
                              photo.person_key === companion.key,
                          )}
                          personType="companion"
                          personKey={companion.key}
                          onUpload={handleUpload}
                          onExtract={handleExtract}
                          busy={uploadPhoto.isPending || extractPhoto.isPending}
                        />
                      </div>
                      <CompanionFields
                        value={companion}
                        onChange={(field, value) => handleCompanionChange(index, field, value)}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : null}

            <Card>
              <CardContent className="space-y-4 p-6">
                <label className="flex items-start gap-3 text-sm text-wood-dark">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(event) => setAcceptedTerms(event.target.checked)}
                    className="mt-1 h-4 w-4"
                  />
                  Confirmo que la información enviada corresponde a los huéspedes de esta
                  reservación.
                </label>
                {submitError ? (
                  <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                    {submitError}
                  </p>
                ) : null}
                <Button
                  size="lg"
                  className="w-full"
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                >
                  {submitCheckIn.isPending ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="mr-2 h-5 w-5" />
                  )}
                  Enviar check-in
                </Button>
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>
    </StandaloneShell>
  )
}

function StandaloneShell({
  hotel,
  children,
}: {
  hotel?: HotelProfile
  children: ReactNode
}) {
  const hotelName = hotel?.hotel_name || 'Hotel Nueva Valladolid'
  const logo = hotel?.logo_image
  return (
    <div className="min-h-screen bg-cream-light">
      <header className="sticky top-0 z-10 border-b border-cream-dark bg-white/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-3xl items-center gap-3 px-4 py-3">
          {logo ? (
            <img src={logo} alt={hotelName} className="h-8 w-8 rounded object-contain" />
          ) : null}
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-wood-dark">{hotelName}</p>
            <p className="truncate text-xs text-stone">Check-in seguro</p>
          </div>
          <ShieldCheck className="ml-auto h-5 w-5 text-terracotta" aria-hidden="true" />
        </div>
      </header>
      <main className="mx-auto w-full max-w-3xl px-4 pb-16 pt-5 sm:pt-6">{children}</main>
    </div>
  )
}

function ReservationSummaryCard({ session }: { session: PublicCheckInSession }) {
  return (
    <Card>
      <CardContent className="grid gap-4 p-6 sm:grid-cols-2">
        <SummaryItem label="Llegada" value={formatDateLong(session.reservation.check_in_date)} />
        <SummaryItem label="Salida" value={formatDateLong(session.reservation.check_out_date)} />
        <SummaryItem label="Habitación" value={`${session.reservation.room_type} #${session.reservation.room_number}`} />
        <SummaryItem
          label="Huéspedes"
          value={`${session.reservation.adults} adulto(s), ${session.reservation.children} niño(s)`}
        />
      </CardContent>
    </Card>
  )
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-stone">{label}</p>
      <p className="mt-1 font-medium text-wood-dark">{value}</p>
    </div>
  )
}

function PrimaryFields({
  value,
  onChange,
}: {
  value: PublicCheckInPrimary
  onChange: (field: keyof PublicCheckInPrimary, value: string) => void
}) {
  const today = new Date().toISOString().slice(0, 10)
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <TextField
        label="Nombre"
        value={value.first_name}
        onChange={(next) => onChange('first_name', next)}
        autoComplete="given-name"
        autoCapitalize="words"
      />
      <TextField
        label="Apellido"
        value={value.last_name}
        onChange={(next) => onChange('last_name', next)}
        autoComplete="family-name"
        autoCapitalize="words"
      />
      <DocumentTypeField value={value.document_type} onChange={(next) => onChange('document_type', next)} />
      <TextField
        label="Número de documento"
        value={value.document_number}
        onChange={(next) => onChange('document_number', next)}
        inputMode="text"
        autoCapitalize="characters"
      />
      <TextField
        label="Fecha de nacimiento"
        type="date"
        value={value.date_of_birth || ''}
        onChange={(next) => onChange('date_of_birth', next)}
        autoComplete="bday"
        max={today}
      />
      <SexField value={value.sex || ''} onChange={(next) => onChange('sex', next)} />
      <TextField
        label="País emisor del documento"
        value={value.document_issue_country || ''}
        onChange={(next) => onChange('document_issue_country', next.toUpperCase())}
        autoCapitalize="characters"
        maxLength={3}
      />
      <TextField
        label="Vencimiento del documento"
        type="date"
        value={value.document_expiration_date || ''}
        onChange={(next) => onChange('document_expiration_date', next)}
      />
      <TextField
        label="Nacionalidad Migración"
        value={value.migration_nationality || ''}
        onChange={(next) => onChange('migration_nationality', next.toUpperCase())}
        autoCapitalize="characters"
        maxLength={3}
      />
      <TextField
        label="Ciudad"
        value={value.city || ''}
        onChange={(next) => onChange('city', next)}
        autoComplete="address-level2"
        autoCapitalize="words"
      />
      <TextField
        label="Procedencia"
        value={value.migration_origin || ''}
        onChange={(next) => onChange('migration_origin', next)}
        autoCapitalize="words"
      />
      <TextField
        label="Destino"
        value={value.migration_destination || defaultMigrationDestination}
        onChange={(next) => onChange('migration_destination', next)}
        autoCapitalize="words"
      />
      <div className="sm:col-span-2">
        <TextField
          label="Dirección"
          value={value.address || ''}
          onChange={(next) => onChange('address', next)}
          autoComplete="street-address"
          autoCapitalize="words"
        />
      </div>
    </div>
  )
}

function CompanionFields({
  value,
  onChange,
}: {
  value: PublicCheckInCompanion
  onChange: (field: keyof PublicCheckInCompanion, value: string) => void
}) {
  const today = new Date().toISOString().slice(0, 10)
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <TextField
        label="Nombre completo"
        value={value.full_name}
        onChange={(next) => onChange('full_name', next)}
        autoComplete="name"
        autoCapitalize="words"
      />
      <DocumentTypeField value={value.document_type} onChange={(next) => onChange('document_type', next)} />
      <TextField
        label="Número de documento"
        value={value.document_number}
        onChange={(next) => onChange('document_number', next)}
        inputMode="text"
        autoCapitalize="characters"
      />
      <SexField value={value.sex || ''} onChange={(next) => onChange('sex', next)} />
      <TextField
        label="Fecha de nacimiento"
        type="date"
        value={value.date_of_birth || ''}
        onChange={(next) => onChange('date_of_birth', next)}
        autoComplete="bday"
        max={today}
      />
      <TextField
        label="País emisor del documento"
        value={value.document_issue_country || ''}
        onChange={(next) => onChange('document_issue_country', next.toUpperCase())}
        autoCapitalize="characters"
        maxLength={3}
      />
      <TextField
        label="Vencimiento del documento"
        type="date"
        value={value.document_expiration_date || ''}
        onChange={(next) => onChange('document_expiration_date', next)}
      />
      <TextField
        label="Nacionalidad Migración"
        value={value.migration_nationality || ''}
        onChange={(next) => onChange('migration_nationality', next.toUpperCase())}
        autoCapitalize="characters"
        maxLength={3}
      />
      <TextField
        label="Procedencia"
        value={value.migration_origin || ''}
        onChange={(next) => onChange('migration_origin', next)}
        autoCapitalize="words"
      />
      <TextField
        label="Destino"
        value={value.migration_destination || defaultMigrationDestination}
        onChange={(next) => onChange('migration_destination', next)}
        autoCapitalize="words"
      />
    </div>
  )
}

type TextFieldProps = {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  inputMode?: 'text' | 'tel' | 'email' | 'numeric' | 'decimal' | 'search' | 'url' | 'none'
  autoComplete?: string
  autoCapitalize?: 'off' | 'none' | 'on' | 'sentences' | 'words' | 'characters'
  max?: string
  maxLength?: number
}

function TextField({
  label,
  value,
  onChange,
  type = 'text',
  inputMode,
  autoComplete,
  autoCapitalize,
  max,
  maxLength,
}: TextFieldProps) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-wood-dark">{label}</span>
      <Input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        inputMode={inputMode}
        autoComplete={autoComplete}
        autoCapitalize={autoCapitalize}
        max={max}
        maxLength={maxLength}
      />
    </label>
  )
}

function SexField({
  value,
  onChange,
}: {
  value: MigrationSex
  onChange: (value: MigrationSex) => void
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-wood-dark">Sexo</span>
      <select
        value={value || ''}
        onChange={(event) => onChange(event.target.value as MigrationSex)}
        className="flex h-10 w-full rounded-md border border-cream-dark bg-white px-3 py-2 text-sm"
      >
        <option value="">Sin dato</option>
        {Object.entries(sexLabels).map(([key, label]) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </select>
    </label>
  )
}

function DocumentTypeField({
  value,
  onChange,
}: {
  value: ReservationDocumentType
  onChange: (value: ReservationDocumentType) => void
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-wood-dark">Tipo de documento</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value as ReservationDocumentType)}
        className="flex h-10 w-full rounded-md border border-cream-dark bg-white px-3 py-2 text-sm"
      >
        {Object.entries(documentLabels).map(([key, label]) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </select>
    </label>
  )
}

function PhotoPanel({
  photos,
  personType,
  personKey,
  onUpload,
  onExtract,
  busy,
}: {
  photos: PublicCheckInPhoto[]
  personType: 'primary' | 'companion'
  personKey: string
  onUpload: (file: File, personType: 'primary' | 'companion', personKey: string) => Promise<void>
  onExtract: (photo: PublicCheckInPhoto) => Promise<void>
  busy: boolean
}) {
  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) void onUpload(file, personType, personKey)
    event.target.value = ''
  }

  const hasPhoto = photos.length > 0
  return (
    <div className="space-y-3">
      <label
        className={
          'flex h-12 w-full cursor-pointer items-center justify-center rounded-md px-4 text-sm font-medium transition-colors ' +
          (hasPhoto
            ? 'border-2 border-terracotta text-terracotta hover:bg-terracotta/10'
            : 'bg-terracotta text-white hover:bg-terracotta/90')
        }
      >
        <Camera className="mr-2 h-5 w-5" />
        {hasPhoto ? 'Tomar otra foto' : 'Tomar foto del documento'}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleFile}
          disabled={busy}
        />
      </label>
      {!hasPhoto ? (
        <p className="text-xs text-stone">Toca el botón para abrir la cámara o elegir una imagen.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {photos.map((photo) => (
            <div key={photo.id} className="rounded-md border border-cream-dark bg-white p-2">
              <img
                src={photo.image_url}
                alt={photo.file_name}
                className="aspect-[4/3] w-full rounded object-cover"
              />
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1 truncate text-xs text-stone">
                  <Upload className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{photo.file_name}</span>
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void onExtract(photo)}
                >
                  {busy ? (
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Wand2 className="mr-1 h-3.5 w-3.5" />
                  )}
                  OCR
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
